/**
 * Google Settings Form Component
 *
 * Form for configuring Google-related settings:
 * - Stale inclusion period (days): How long URLs not pinged to Google remain eligible
 * - Custom quota limit override: Override default 200/day quota if merchant got increase
 *
 * Only shown when Google is connected. Includes disconnect functionality.
 *
 * @see docs/PRD.md for settings requirements
 * @see app/src/pages/types/api.ts for MerchantSettings type
 */

import { useState, useCallback, useEffect } from 'react';
import {
  Card,
  Text,
  BlockStack,
  InlineStack,
  Button,
  TextField,
  Banner,
  Box,
  Divider,
  Modal,
  Badge,
} from '@shopify/polaris';
import type { GoogleConnectionStatus, MerchantSettings, ApiResult } from '../types/api';
import { updateSettings, disconnectGoogle } from '../lib/api-client';

// =============================================================================
// Types
// =============================================================================

export interface GoogleSettingsFormProps {
  /** Google connection status - form only renders when connected */
  googleStatus: GoogleConnectionStatus | null;
  /** Current merchant settings */
  settings: MerchantSettings | null;
  /** Whether data is loading */
  loading?: boolean;
  /** Error message if fetch failed */
  error?: string | null;
  /** Callback when settings are saved or Google is disconnected */
  onSettingsChange?: () => void;
  /** Callback when Google is disconnected */
  onGoogleDisconnect?: () => void;
}

// =============================================================================
// Constants
// =============================================================================

/** Minimum stale inclusion period in days */
const STALE_PERIOD_MIN = 1;

/** Maximum stale inclusion period in days */
const STALE_PERIOD_MAX = 30;

/** Default stale inclusion period in days */
const STALE_PERIOD_DEFAULT = 7;

/** Maximum quota limit (matches Google's typical max for approved accounts) */
const QUOTA_LIMIT_MAX = 200;

/** Minimum quota limit */
const QUOTA_LIMIT_MIN = 1;

/** Default quota limit */
const QUOTA_LIMIT_DEFAULT = 200;

// =============================================================================
// Validation Helpers
// =============================================================================

/**
 * Validate stale inclusion period input
 */
function validateStalePeriod(value: string): string | null {
  if (value === '') return null; // Empty is valid (will use default)

  const num = parseInt(value, 10);

  if (isNaN(num)) {
    return 'Please enter a valid number';
  }

  if (num < STALE_PERIOD_MIN) {
    return `Minimum is ${STALE_PERIOD_MIN} day`;
  }

  if (num > STALE_PERIOD_MAX) {
    return `Maximum is ${STALE_PERIOD_MAX} days`;
  }

  return null;
}

/**
 * Validate quota limit input
 */
function validateQuotaLimit(value: string): string | null {
  if (value === '') return null; // Empty is valid (uses default)

  const num = parseInt(value, 10);

  if (isNaN(num)) {
    return 'Please enter a valid number';
  }

  if (num < QUOTA_LIMIT_MIN) {
    return `Minimum is ${QUOTA_LIMIT_MIN}`;
  }

  if (num > QUOTA_LIMIT_MAX) {
    return `Maximum is ${QUOTA_LIMIT_MAX}`;
  }

  return null;
}

// =============================================================================
// Main Component
// =============================================================================

/**
 * Form for configuring Google-related settings.
 *
 * Features:
 * - Only renders when Google is connected
 * - Stale inclusion period: number input (1-30 days, default 7)
 * - Custom quota limit: number input (optional override, max 200)
 * - Input validation with error messages
 * - Save button with success/error handling
 * - Disconnect Google button with confirmation modal
 *
 * @example
 * ```tsx
 * <GoogleSettingsForm
 *   googleStatus={googleStatus}
 *   settings={merchantSettings}
 *   loading={isLoading}
 *   onSettingsChange={handleRefresh}
 *   onGoogleDisconnect={handleDisconnect}
 * />
 * ```
 */
export function GoogleSettingsForm({
  googleStatus,
  settings,
  loading = false,
  error = null,
  onSettingsChange,
  onGoogleDisconnect,
}: GoogleSettingsFormProps) {
  // Form state
  const [stalePeriod, setStalePeriod] = useState<string>('');
  const [quotaLimit, setQuotaLimit] = useState<string>('');

  // Validation errors
  const [stalePeriodError, setStalePeriodError] = useState<string | null>(null);
  const [quotaLimitError, setQuotaLimitError] = useState<string | null>(null);

  // Save state
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Disconnect modal state
  const [disconnectModalOpen, setDisconnectModalOpen] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [disconnectError, setDisconnectError] = useState<string | null>(null);

  // Track if form has unsaved changes
  const [hasChanges, setHasChanges] = useState(false);

  // Initialize form values from settings
  useEffect(() => {
    if (settings) {
      // Convert staleInclusionDays to string, handling type union
      const staleDays = settings.staleInclusionDays;
      setStalePeriod(staleDays?.toString() ?? STALE_PERIOD_DEFAULT.toString());

      // Only show quota if it differs from default
      const quota = settings.googleQuotaLimit;
      setQuotaLimit(quota === QUOTA_LIMIT_DEFAULT ? '' : quota?.toString() ?? '');

      setHasChanges(false);
    }
  }, [settings]);

  // Handle stale period change
  const handleStalePeriodChange = useCallback((value: string) => {
    // Only allow numeric input
    const cleaned = value.replace(/[^0-9]/g, '');
    setStalePeriod(cleaned);
    setStalePeriodError(validateStalePeriod(cleaned));
    setSaveSuccess(false);
    setHasChanges(true);
  }, []);

  // Handle quota limit change
  const handleQuotaLimitChange = useCallback((value: string) => {
    // Only allow numeric input
    const cleaned = value.replace(/[^0-9]/g, '');
    setQuotaLimit(cleaned);
    setQuotaLimitError(validateQuotaLimit(cleaned));
    setSaveSuccess(false);
    setHasChanges(true);
  }, []);

  // Handle save
  const handleSave = useCallback(async () => {
    // Validate all fields
    const staleError = validateStalePeriod(stalePeriod);
    const quotaError = validateQuotaLimit(quotaLimit);

    setStalePeriodError(staleError);
    setQuotaLimitError(quotaError);

    if (staleError || quotaError) {
      return;
    }

    setSaving(true);
    setSaveError(null);
    setSaveSuccess(false);

    try {
      // Build update object
      const updates: { staleInclusionDays?: number; googleQuotaLimit?: number } = {};

      // Parse stale period
      const stalePeriodNum = stalePeriod ? parseInt(stalePeriod, 10) : STALE_PERIOD_DEFAULT;
      // Clamp to valid values (1, 3, 7 are the PRD options, but we accept 1-30 per task spec)
      updates.staleInclusionDays = stalePeriodNum;

      // Parse quota limit (empty means use default)
      const quotaLimitNum = quotaLimit ? parseInt(quotaLimit, 10) : QUOTA_LIMIT_DEFAULT;
      updates.googleQuotaLimit = quotaLimitNum;

      const result: ApiResult<void> = await updateSettings(updates);

      if (result.success) {
        setSaveSuccess(true);
        setHasChanges(false);
        onSettingsChange?.();

        // Clear success message after 3 seconds
        setTimeout(() => setSaveSuccess(false), 3000);
      } else {
        setSaveError(result.error.error);
      }
    } catch (err) {
      setSaveError('Failed to save settings. Please try again.');
    } finally {
      setSaving(false);
    }
  }, [stalePeriod, quotaLimit, onSettingsChange]);

  // Handle disconnect
  const handleDisconnect = useCallback(async () => {
    setDisconnecting(true);
    setDisconnectError(null);

    try {
      const result: ApiResult<void> = await disconnectGoogle();

      if (result.success) {
        setDisconnectModalOpen(false);
        onGoogleDisconnect?.();
        onSettingsChange?.();
      } else {
        setDisconnectError(result.error.error);
      }
    } catch (err) {
      setDisconnectError('Failed to disconnect. Please try again.');
    } finally {
      setDisconnecting(false);
    }
  }, [onGoogleDisconnect, onSettingsChange]);

  // Handle modal close
  const handleModalClose = useCallback(() => {
    if (!disconnecting) {
      setDisconnectModalOpen(false);
      setDisconnectError(null);
    }
  }, [disconnecting]);

  // Don't render if Google is not connected
  if (!googleStatus?.connected) {
    return null;
  }

  // Loading state
  if (loading) {
    return (
      <Card>
        <BlockStack gap="400">
          <Text as="h3" variant="headingSm">
            Google Settings
          </Text>
          <Text as="p" variant="bodyMd" tone="subdued">
            Loading settings...
          </Text>
        </BlockStack>
      </Card>
    );
  }

  // Error state
  if (error) {
    return (
      <Card>
        <BlockStack gap="300">
          <Text as="h3" variant="headingSm">
            Google Settings
          </Text>
          <Banner tone="critical">
            {error}
          </Banner>
        </BlockStack>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <BlockStack gap="500">
          {/* Header */}
          <InlineStack align="space-between" blockAlign="center">
            <Text as="h3" variant="headingSm">
              Google Settings
            </Text>
            <Badge tone="success">Connected</Badge>
          </InlineStack>

          {/* Stale Inclusion Period */}
          <TextField
            label="Stale inclusion period (days)"
            type="number"
            value={stalePeriod}
            onChange={handleStalePeriodChange}
            error={stalePeriodError ?? undefined}
            helpText="URLs not pinged to Google within this period are eligible for re-ping"
            min={STALE_PERIOD_MIN}
            max={STALE_PERIOD_MAX}
            autoComplete="off"
            placeholder={STALE_PERIOD_DEFAULT.toString()}
          />

          {/* Custom Quota Limit */}
          <TextField
            label="Custom quota limit"
            type="number"
            value={quotaLimit}
            onChange={handleQuotaLimitChange}
            error={quotaLimitError ?? undefined}
            helpText="Leave blank to use default limit. Set lower if experiencing rate limits."
            min={QUOTA_LIMIT_MIN}
            max={QUOTA_LIMIT_MAX}
            autoComplete="off"
            placeholder={`Default: ${QUOTA_LIMIT_DEFAULT}`}
          />

          {/* Save feedback */}
          {saveError && (
            <Banner tone="critical" onDismiss={() => setSaveError(null)}>
              {saveError}
            </Banner>
          )}

          {saveSuccess && (
            <Banner tone="success" onDismiss={() => setSaveSuccess(false)}>
              Settings saved successfully
            </Banner>
          )}

          {/* Save button */}
          <Box>
            <Button
              variant="primary"
              onClick={handleSave}
              loading={saving}
              disabled={!hasChanges || !!stalePeriodError || !!quotaLimitError}
            >
              Save Settings
            </Button>
          </Box>

          <Divider />

          {/* Disconnect section */}
          <BlockStack gap="200">
            <Text as="p" variant="bodyMd" fontWeight="semibold">
              Disconnect Google
            </Text>
            <Text as="p" variant="bodySm" tone="subdued">
              Remove your Google account connection. IndexNow pings will continue working.
            </Text>
            <Box>
              <Button
                variant="plain"
                tone="critical"
                onClick={() => setDisconnectModalOpen(true)}
              >
                Disconnect Google Account
              </Button>
            </Box>
          </BlockStack>
        </BlockStack>
      </Card>

      {/* Disconnect confirmation modal */}
      <Modal
        open={disconnectModalOpen}
        onClose={handleModalClose}
        title="Disconnect Google?"
        primaryAction={{
          content: 'Disconnect',
          destructive: true,
          loading: disconnecting,
          onAction: handleDisconnect,
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            disabled: disconnecting,
            onAction: handleModalClose,
          },
        ]}
      >
        <Modal.Section>
          <BlockStack gap="300">
            <Text as="p" variant="bodyMd">
              This will remove your Google account connection. Pingdex will no longer
              ping Google's Indexing API when your content changes.
            </Text>
            <Text as="p" variant="bodyMd">
              IndexNow pings (Bing, Yandex, etc.) will continue working normally.
            </Text>
            {disconnectError && (
              <Banner tone="critical">
                {disconnectError}
              </Banner>
            )}
          </BlockStack>
        </Modal.Section>
      </Modal>
    </>
  );
}
